import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { BufferGeometry, Float32BufferAttribute, Points, PointsMaterial, Vector3 } from 'three';
import { useEffect } from 'react';

export function Scene({ scrollY = 0 }) {
  const pointCloudRef = useRef(null);
  const dynamicPointsRef = useRef(null);
  const time = useRef(0);
  const scanLineRef = useRef(0);
  const originalPositions = useRef(null);
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (scrollY > 0) {
      hasScrolled.current = true;
    }
  }, [scrollY]);

  useEffect(() => {
    if (pointCloudRef.current) {
      const geometry = new BufferGeometry();
      const points = [];
      const colors = [];

      // Generate terrain points with white color
      for (let x = -15; x < 15; x += 0.15) {
        for (let z = -15; z < 15; z += 0.1) {
          let y = Math.sin(x * 0.2) * 2 + Math.cos(z * 0.3) * 2;
          y += Math.sin(x * 0.5 + z * 0.5) * 1.5;

          y += Math.exp(-Math.pow(x + 5, 2) * 0.1) * 3;
          y += Math.exp(-Math.pow(z - 2, 2) * 0.05) * 2;

          const pitCenter = new Vector3(-2, 0, 0);
          const distanceToPit = new Vector3(x, 0, z).distanceTo(pitCenter);
          if (distanceToPit < 5) {
            const depth = Math.max(0, 5 - distanceToPit);
            y -= Math.pow(depth, 1.5) * (1 + Math.sin(x * 2) * 0.2);
          }

          const noise = (Math.sin(x * 5) * Math.cos(z * 5) * 0.1) +
            (Math.sin(x * 10) * Math.cos(z * 10) * 0.05) +
            (Math.random() - 0.5) * 0.1;

          const slopeFactor = Math.abs(
            Math.atan2(Math.sin(x * 0.2) * 2, Math.cos(z * 0.3) * 2)
          );

          if (Math.random() > 0.2 + slopeFactor * 0.3) {
            points.push(x, y + noise, z);
            colors.push(1, 1, 1);
          }
        }
      }

      // Mining shaft with white color
      for (let y = -8; y < 1; y += 0.1) { // Vertical Pipe particles
        const shaftX = -2;
        const shaftZ = 0;
        for (let i = 0; i < 24; i++) {
          const angle = (i / 24) * Math.PI * 2;
          const radius = 0.4 + Math.sin(y * 2) * 0.05;
          const x = shaftX + Math.cos(angle) * radius;
          const z = shaftZ + Math.sin(angle) * radius;

          if (Math.random() > 0.3) {
            points.push(x, y, z);
            colors.push(1, 1, 1);
          }

          if (Math.abs(y % 1) < 0.1) {
            for (let r = 0; r < 3; r++) {
              const supportRadius = radius + r * 0.1;
              points.push(
                x + Math.cos(angle) * supportRadius,
                y,
                z + Math.sin(angle) * supportRadius
              );
              colors.push(1, 1, 1);
            }
          }
        }
      }

      // Horizontal tunnels with white color
      const tunnelLevels = [-7, -5, -3, -1];
      tunnelLevels.forEach((level, index) => {
        const tunnelLength = 8 + index * 2;
        for (let x = -tunnelLength; x < 2; x += 0.1) { //Horizantal pipe particles
          const baseRadius = 0.4 + Math.sin(x * 0.5) * 0.05;
          for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const radius = baseRadius * (1 + Math.sin(x * 2) * 0.1);
            const y = level + Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;

            if (Math.random() > 0.3) {
              points.push(x, y, z);
              colors.push(1, 1, 1);
            }
          }

          if (Math.abs(x % 0.5) < 0.1) {
            for (let i = 0; i < 12; i++) {
              const supportAngle = (i / 12) * Math.PI * 2;
              const supportY = level + Math.cos(supportAngle) * 0.45;
              const supportZ = Math.sin(supportAngle) * 0.45;
              points.push(x, supportY, supportZ);
              colors.push(1, 1, 1);
            }
          }
        }
      });

      // Surface infrastructure with white color
      const buildingPatterns = [
        { size: 5, height: 3, spacing: 0.08 },  // Compact & taller
        { size: 1.2, height: 2, spacing: 0.06 }, // Denser smaller buildings
        { size: 0.8, height: 1.5, spacing: 0.04 } // Very close-packed
      ];

      buildingPatterns.forEach(pattern => {
        for (let x = -pattern.size; x < pattern.size; x += pattern.spacing) {
          for (let z = -pattern.size; z < pattern.size; z += pattern.spacing) {
            if (Math.abs(x) < pattern.size * 0.9 && Math.abs(z) < pattern.size * 0.9) {
              const height = pattern.height * (0.9 + Math.random() * 0.3); // Small variation
              points.push(x - 5, height, z + 5);
              colors.push(1, 1, 1);
            }
          }
        }
      });

      geometry.setAttribute('position', new Float32BufferAttribute(points, 3));
      geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
      pointCloudRef.current.geometry = geometry;

      // Store original positions for dispersion effect
      originalPositions.current = points.slice();

      // Dynamic points with white color
      const dynamicGeometry = new BufferGeometry();
      const dynamicPoints = [];
      const dynamicColors = [];

      for (let x = -15; x < 15; x += 0.1) {  //No of wave particles
        for (let z = -15; z < 15; z += 0.1) {//No of wave particles
          if (Math.random() > 0.8) {
            dynamicPoints.push(x, 0, z);
            dynamicColors.push(1, 1, 1);
          }
        }
      }

      dynamicGeometry.setAttribute('position', new Float32BufferAttribute(dynamicPoints, 3));
      dynamicGeometry.setAttribute('color', new Float32BufferAttribute(dynamicColors, 3));
      if (dynamicPointsRef.current) {
        dynamicPointsRef.current.geometry = dynamicGeometry;
      }
    }
  }, []);

  useFrame((state, delta) => {
    const xOffset = 1; // Adjust X-axis position
    const yOffset = 1; // Adjust Y-axis position
    const zOffset = 1; // Adjust Z-axis position

    time.current += delta;
    scanLineRef.current += delta * 2;

    // Set initial position BEFORE rotation
    if (pointCloudRef.current) {
      pointCloudRef.current.position.set(xOffset, yOffset, zOffset);
      pointCloudRef.current.rotation.set(-Math.PI / 6, 0.5, 0.2); // Rotation applied AFTER setting position
    }

    if (dynamicPointsRef.current) {
      dynamicPointsRef.current.position.set(xOffset, yOffset, zOffset);
      dynamicPointsRef.current.rotation.set(-Math.PI / 1, 0, 0);
    }

    if (pointCloudRef.current && originalPositions.current) {
      const positions = pointCloudRef.current.geometry.attributes.position;
      const scrollFactor = Math.min(scrollY / 300, 3); // Adjusted scroll sensitivity

      for (let i = 0; i < positions.count; i++) {
        const idx = i * 3;
        const originalX = originalPositions.current[idx];
        const originalY = originalPositions.current[idx + 1];
        const originalZ = originalPositions.current[idx + 2];
        // Create more dynamic dispersion effect
        const timeOffset = time.current + i * 0.01;
        const noise = Math.sin(timeOffset) * scrollFactor;
        const angle = (i % 360) * (Math.PI / 180);
        // Add vertical displacement based on scroll
        const verticalOffset = Math.sin(timeOffset * 2) * scrollFactor * 0.5;

        positions.setXYZ(
          i,
          originalX + xOffset + Math.cos(angle) * noise * 1.5,
          originalY + yOffset + verticalOffset + (Math.random() - 0.5) * noise * 2,
          originalZ + zOffset + Math.sin(angle) * noise * 1.5
        );
      }

      positions.needsUpdate = true;
    }

    if (dynamicPointsRef.current) {
      const positions = dynamicPointsRef.current.geometry.attributes.position;
      const colors = dynamicPointsRef.current.geometry.attributes.color;

      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const z = positions.getZ(i);

        const waveXOffset = -5;  // Shift wave to the left (-) or right (+)
        const waveZOffset = 3;   // Shift wave forward (+) or backward (-)
        
        const adjustedX = x + waveXOffset;
        const adjustedZ = z + waveZOffset;
        
        const yOffset = -10; // Change this value to move the wave up/down

        const wave = Math.sin(x * 0.5 + scanLineRef.current) *
          Math.cos(z * 0.5 + scanLineRef.current) * 0.9;
        
        positions.setY(i, yOffset + wave);
        

        colors.setXYZ(i, 1, 1, 1);
      }

      positions.needsUpdate = true;
      colors.needsUpdate = true;
    }
  });


  return (
    <>
      <points ref={pointCloudRef}>
        <pointsMaterial
          vertexColors={true}
          size={0.05}
          sizeAttenuation={true}
          transparent={true}
          opacity={0.9}
          fog={true}
        />
      </points>
      <points ref={dynamicPointsRef}>
        <pointsMaterial
          vertexColors={true}
          size={0.01}
          sizeAttenuation={true}
          transparent={true}
          opacity={0.6}
          fog={true}
        />
      </points>
    </>
  );
}